import React, { useCallback, useEffect, useMemo, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";
import { DateObject } from "../../../../../domain/fundamental/DateObject";
import { Month } from "../../../../../domain/fundamental/Month";
import { useBoolean } from "../../../../hooks/common/useBoolean";
import { Button } from "../../../common/button/Button";
import { SingleCalendar } from "../../../dash_board/components/SingleCalendar";
import { CalendarIcon } from "../../../dash_board/icons/CalendarIcon";

// 終了日の自動設定値は28日
const autoDateDefaultValue = 28;

export const DateSelector = React.memo(function DateSelector(props: Props) {
    const { defaultStartDate, defaultEndDate, handleSaveClicked, dataSyncStartDate } = props;

    const [startDate, setStartDate] = useState(defaultStartDate);

    const [endDate, setEndDate] = useState(defaultEndDate);

    const [month, setMonth] = useState(defaultStartDate?.getMonth() || Month.thisMonth());

    const nextMonthDisabled = useMemo(() => month.next().isGreaterThan(DateObject.today().getMonth()), [month]);

    const [openCalendar, , close, toggle] = useBoolean();

    const clickDate = useCallback((date: DateObject) => {
        if (startDate && endDate) {
            setStartDate(date);
            setEndDate(null);
        }

        if (!startDate && !endDate) {
            setStartDate(date);
        }

        if (startDate && !endDate) {
            setEndDate(date);
        }
    }, [startDate, endDate]);

    const onSaveClicked = useCallback(() => {
        if (startDate && endDate) {
            handleSaveClicked(startDate, endDate);
            close();
        }
    }, [startDate, endDate, handleSaveClicked, close]);

    const autoSetEndDate = useCallback(() => {
        if (!startDate || !!(startDate && endDate)) return;
        setEndDate(startDate.addDay(autoDateDefaultValue - 1).isGreaterThanToday() ? DateObject.today() : startDate.addDay(autoDateDefaultValue - 1));
    }, [startDate, endDate]);

    const dateDisabled = useCallback((date: DateObject) => {
        // 未来日は設定不可
        if (date.isGreaterThanToday()) return true;

        // 開始日を入力する時はデータ連携開始日以降を選ばせる。データ連携開始日未設定の場合は制限なし。
        if ((startDate && endDate) || (!startDate && !endDate)) {
            return !!(dataSyncStartDate && date.isLessThan(dataSyncStartDate));
        }

        // 終了日を入力する時は開始日より後を選ばせる。
        if (startDate && !endDate) {
            return date.isLessThan(startDate);
        }

        return true;
    }, [dataSyncStartDate, startDate, endDate]);

    const setPreviousMonth = useCallback(() => setMonth(month => month.previous()), []);

    const setNextMonth = useCallback(() => setMonth(month => month.next()), []);

    const doNothing = useCallback(() => {}, []);

    useEffect(() => {
        if (openCalendar) {
            setStartDate(defaultStartDate);
            setEndDate(defaultEndDate);
        }
    }, [openCalendar, defaultStartDate, defaultEndDate]);

    return (
        <div className="period_selector">
            <div className="period">
                <div className="date">{defaultStartDate?.format() ?? "-"}</div> 〜<div className="date">{defaultEndDate?.format() ?? "-"}</div>
                <button onClick={toggle} className="calendar_button">
                    <CalendarIcon />
                </button>
            </div>
            {openCalendar &&
                <OutsideClickHandler onOutsideClick={close}>
                    <div className={"calendars_container"}>
                        <div className={"buttons"}>
                            {/* 確定して閉じるボタン */}
                            <Button
                                disabled={!startDate || !endDate}
                                label={"確定"}
                                onClick={onSaveClicked}
                                height={25}
                                width={50}
                            />
                            {/* 開始日含め30日を設定するボタン */}
                            <Button
                                colorReversed
                                label={`${autoDateDefaultValue}日設定`}
                                onClick={autoSetEndDate}
                                height={25}
                                width={100}
                            />
                            {/* 設定せずに閉じるボタン */}
                            <Button
                                colorReversed
                                label={"キャンセル"}
                                onClick={close}
                                height={25}
                                width={100}
                            />
                        </div>
                        <div className="calendars">
                            <SingleCalendar
                                startDate={startDate || dataSyncStartDate || DateObject.today()}
                                endDate={endDate || DateObject.today()}
                                onClickDate={clickDate}
                                month={month}
                                hoverDate={null}
                                setHoverDate={doNothing}
                                nextMonthDisabled
                                previousMonth={setPreviousMonth}
                                dateDisabled={dateDisabled}
                            />
                            <SingleCalendar
                                startDate={startDate || dataSyncStartDate || DateObject.today()}
                                endDate={endDate || DateObject.today()}
                                onClickDate={clickDate}
                                month={month.next()}
                                hoverDate={null}
                                setHoverDate={doNothing}
                                previousMonthDisabled
                                nextMonth={setNextMonth}
                                nextMonthDisabled={nextMonthDisabled}
                                dateDisabled={dateDisabled}
                            />
                        </div>
                    </div>
                </OutsideClickHandler>
            }
        </div>
    );
});

type Props = {
    defaultStartDate: DateObject | null,
    defaultEndDate: DateObject | null,
    handleSaveClicked: (startDate: DateObject, endDate: DateObject) => void,
    dataSyncStartDate?: DateObject | null
}
